import { useState, useEffect, useCallback } from 'react';
import { ENDPOINTS, normalizeCarriers, normalizeDataUsage } from '../api/api';

const safe = (p) => p.catch(() => null);

export function useCircuitData(circuitName, page = 1, search = '') {
  const [state, setState] = useState({ loading: true, devicesLoading: true, error: null });
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!circuitName) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    const name = circuitName.toUpperCase();

    Promise.all([
      fetch(ENDPOINTS.circuit(name)).then((r) => r.json()),
      safe(fetch(ENDPOINTS.circuitDataUsage(name)).then((r) => r.json())),
      safe(fetch(ENDPOINTS.circuitByGrade(name)).then((r) => r.json())),
    ])
      .then(([summary, dataUsage, byGrade]) => {
        if (!summary) {
          setState((s) => ({ ...s, loading: false, error: 'Failed to load circuit data' }));
          return;
        }
        const schools = (Array.isArray(summary.devicesPerSchool) ? summary.devicesPerSchool : [])
          .sort((a, b) => b.count - a.count);

        setState((s) => ({
          ...s,
          loading: false,
          error: null,
          stats: {
            devices: (summary.totalDevices ?? 0).toLocaleString(),
            schools: (summary.totalNames   ?? 0).toLocaleString(),
          },
          schools,
          byGrade:   Array.isArray(byGrade) ? byGrade : [],
          carriers:  normalizeCarriers(summary.carrierStats ?? []),
          dataUsage: normalizeDataUsage(Array.isArray(dataUsage) ? dataUsage : []),
        }));
      })
      .catch((err) => setState((s) => ({ ...s, loading: false, error: err.message })));
  }, [circuitName, refreshKey]);

  useEffect(() => {
    if (!circuitName) return;
    setState((s) => ({ ...s, devicesLoading: true }));
    const name = circuitName.toUpperCase();

    safe(fetch(ENDPOINTS.circuitDevices(name, page, 10, search)).then((r) => r.json()))
      .then((paginated) => {
        setState((s) => ({
          ...s,
          devicesLoading: false,
          devices:    paginated?.devices   ?? [],
          totalPages: paginated?.totalPages ?? 1,
        }));
      });
  }, [circuitName, page, search, refreshKey]);

  return { ...state, refresh };
}
